import React, { useState } from 'react';
import { ArrowRight, Check, Mail, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { getAttribution, track } from '../lib/analytics';

const CONTACT_EMAIL = 'sales@briefingiq.com';

const ContactPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', role: '', product: 'both', message: ''
  });
  const [website, setWebsite] = useState('');
  const [startedAt] = useState(() => Date.now());

  const change = (f) => (e) => setForm({ ...form, [f]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', { ...form, source: 'contact_page', website, form_started_at: startedAt, attribution: getAttribution() });
      track('contact_submit', { label: form.product, product: form.product });
      setDone(true);
      toast({ title: 'Message sent', description: 'Our team will reach out shortly.' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Contact BriefingIQ. Talk to sales, request a demo, or send a general enquiry. Our team responds within one business day."
        path="/contact"
        keywords={['contact BriefingIQ', 'sales', 'demo request']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Contact', path: '/contact' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact BriefingIQ',
          url: (typeof window !== 'undefined' ? window.location.origin : '') + '/contact',
          mainEntity: {
            '@type': 'Organization',
            name: 'BriefingIQ, Inc.',
            email: 'sales@briefingiq.com',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'San Ramon',
              addressRegion: 'California',
              addressCountry: 'US',
            },
            contactPoint: [{
              '@type': 'ContactPoint',
              email: 'sales@briefingiq.com',
              contactType: 'sales',
              areaServed: 'Worldwide',
              availableLanguage: 'English',
            }],
          },
        }}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">Contact</div>
          <h1 className="headline-xl text-ink mb-6">Let&apos;s talk.</h1>
          <p className="text-lg text-ink-2 max-w-2xl leading-relaxed">
            Tell us about your program. We&apos;ll get back to you within one business day.
          </p>
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-2">Email</div>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-lg text-ink hover:underline inline-flex items-center gap-2"
              >
                <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
              </a>
              <p className="text-sm text-ink-3 mt-2">Sales, partnerships, and general enquiries.</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-2">Response time</div>
              <div className="text-ink">One business day, typically faster.</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-2">Offices</div>
              <div className="text-ink mb-3">San Ramon, California</div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=San+Ramon,+California"
                target="_blank"
                rel="noreferrer"
                className="block relative rounded-xl overflow-hidden border border-[var(--line-2)] bg-[var(--bg-alt)] group"
                aria-label="Open map of San Ramon, California"
                style={{ aspectRatio: '16 / 10' }}
              >
                <iframe
                  title="San Ramon, California map"
                  src="https://www.google.com/maps?q=San+Ramon,+California&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ border: 0, filter: 'grayscale(1) contrast(1.05) brightness(0.98)' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-widest text-ink-3 bg-white/85 backdrop-blur px-2 py-0.5 rounded">
                  Open map
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-white rounded-2xl border border-[var(--line-2)] p-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[var(--bg-alt)] flex items-center justify-center mb-5">
                  <Check className="w-6 h-6 text-ink" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-semibold text-ink mb-2 tracking-tight">Message received.</h3>
                <p className="text-sm text-ink-2">A member of our team will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl border border-[var(--line-2)] p-8 space-y-4" data-testid="contact-form">
                <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden opacity-0 pointer-events-none" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="contact-honeypot" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Full name</Label>
                    <Input required value={form.name} onChange={change('name')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Work email</Label>
                    <Input type="email" required value={form.email} onChange={change('email')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="jane@company.com" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Phone (optional)</Label>
                    <Input type="tel" autoComplete="tel" value={form.phone} onChange={change('phone')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="+1 (555) 000-0000" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Company</Label>
                    <Input value={form.company} onChange={change('company')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="Acme Corp" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Role</Label>
                    <Input value={form.role} onChange={change('role')} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="Briefing Manager" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Interested in</Label>
                    <Select value={form.product} onValueChange={change('product')}>
                      <SelectTrigger className="mt-1.5 rounded-lg border-[var(--line-2)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="briefingiq">BriefingIQ — Executive briefings</SelectItem>
                        <SelectItem value="eventiq">EventIQ — Corporate events</SelectItem>
                        <SelectItem value="both">Both products</SelectItem>
                        <SelectItem value="other">Other / general enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-ink-3">Message</Label>
                  <Textarea required value={form.message} onChange={change('message')} rows={5} className="mt-1.5 rounded-lg border-[var(--line-2)]" placeholder="Tell us about your program…" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="contact-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[10px] text-ink-3 text-center">
                  Messages are routed to {CONTACT_EMAIL}.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
